import { BreakpointObserver } from '@angular/cdk/layout';
import { DatePipe, TitleCasePipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as QRCode from 'qrcode';
import { map, Subject, takeUntil } from 'rxjs';
import { AuthService, JoinLinkItem, PendingInvitation } from 'src/app/auth.service';
import { ImageDialogComponent } from 'src/app/shared/components/image-dialog/image-dialog.component';
import { RoleOption, RolePickerComponent } from 'src/app/shared/components/role-picker/role-picker.component';
import { SelectOption } from 'src/app/shared/components/ui/select/select.component';
import { SkeletonComponent } from 'src/app/shared/components/ui/skeleton/skeleton.component';
import { ConfirmMenuService } from 'src/app/shared/services/confirm-menu/confirm-menu.service';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { GroupsService } from 'src/app/views/groups/groups.service';
import { JoinLinkDialogComponent } from './join-link-dialog/join-link-dialog.component';
import { UserCreateComponent } from './user-create/user-create.component';

interface ManagedUser {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  picture: string;
  role: string;
  isOwner: boolean;
  uid: string;
}

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatFormField,
    MatInput,
    MatPrefix,
    MatButton,
    MatIconButton,
    MatIcon,
    MatProgressSpinner,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    TranslateModule,
    RolePickerComponent,
    DatePipe,
    TitleCasePipe,
    MatTooltipModule,
    MatSlideToggle,
    SkeletonComponent,
    MatChipsModule,
  ],
})
export class UserManagementComponent implements OnInit, AfterViewInit, OnDestroy {
  public sidenavService = inject(SidenavService);
  private destroy$ = new Subject<void>();
  private groupsService = inject(GroupsService);

  isSmallSceen = signal<boolean>(false);
  groupOptions = signal<SelectOption[]>([]);
  joinLinks = signal<JoinLinkItem[]>([]);
  availableGroupsList = signal<{ _id: string; name: string }[]>([]);
  selectedJoinLinkGroupId = signal<string | null>(null);

  constructor() {
    this.breakpointObserver
      .observe(['(max-width: 960px)'])
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isSmallSceen.set(result.matches);
      });
    const isSmall = this.isSmallSceen();
    this.sidenavService.showGlobalToggle.set(!isSmall);
  }

  private authService = inject(AuthService);
  private dialog = inject(MatDialog);
  private confirmMenuService = inject(ConfirmMenuService);
  private snackbar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private breakpointObserver = inject(BreakpointObserver);

  showTiles = toSignal(this.breakpointObserver.observe('(max-width: 960px)').pipe(map((r) => r.matches)), { initialValue: false });

  joinUrl = signal<string>('');
  joinLinkEnabled = signal<boolean>(true);
  showJoinLinkBanner = signal<boolean>(false);
  joinLinksLoading = signal<boolean>(false);

  displayedColumns = ['email', 'fullName', 'role', 'actions'];
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('tableContainer') tableContainer!: ElementRef;

  roleOptions: RoleOption[] = [
    { value: 'admin', label: 'Roles.Admin' },
    { value: 'writer', label: 'Roles.Writer' },
    { value: 'reader', label: 'Roles.Reader' },
  ];

  searchInput = '';
  loading = signal(false);
  loadingMore = signal(false);
  currentUserData: any = null;
  usersDataSource = new MatTableDataSource<ManagedUser>([]);
  totalLength = 0;

  // Pending invitations
  pendingInvitations = signal<PendingInvitation[]>([]);
  resendingIds = signal<Set<string>>(new Set());
  showPendingInvitationsBanner = signal<boolean>(false);
  pendingInvitationsLoading = signal<boolean>(false);

  // Infinite Scroll Pagination State
  pageIndex = 0;
  pageSize = 30;
  hasMoreData = true;

  sortField = '';
  sortDirection: 'asc' | 'desc' | '' = 'asc';
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.loadCurrentUserData();
    this.getUsers(true);
    this.loadPendingInvitations();
    this.loadGroups();
  }

  loadGroups() {
    this.groupsService.getGroups().subscribe({
      next: (groups: any[]) => {
        this.availableGroupsList.set(groups || []);
        const opts: SelectOption[] = [{ value: null, label: 'UserManagement.DefaultGeneralGroup' }, ...(groups || []).map((g) => ({ value: g._id, label: g.name }))];
        this.groupOptions.set(opts);
      },
      error: () => {
        this.groupOptions.set([{ value: null, label: 'UserManagement.DefaultGeneralGroup' }]);
      },
    });
  }

  ngAfterViewInit() {
    this.usersDataSource.sort = this.sort;
  }

  loadCurrentUserData(): void {
    const currentUser = this.authService.getCurrentUser();
    if (currentUser) {
      this.currentUserData = currentUser;
    }
  }

  getUsers(reset = false) {
    if (reset) {
      this.pageIndex = 0;
      this.usersDataSource.data = [];
      this.loading.set(true);
      this.hasMoreData = true;
    } else {
      this.loadingMore.set(true);
    }

    this.authService.getWorkspaceUsers(this.pageSize, this.pageIndex, this.searchInput, this.sortField, this.sortDirection as string).subscribe({
      next: (res: { list: ManagedUser[]; total: number }) => {
        if (reset) {
          this.usersDataSource.data = res.list;
        } else {
          this.usersDataSource.data = [...this.usersDataSource.data, ...res.list];
        }

        this.totalLength = res.total;
        this.hasMoreData = this.usersDataSource.data.length < this.totalLength;

        this.loading.set(false);
        this.loadingMore.set(false);
      },
      error: (err) => {
        console.error(err);
        this.loading.set(false);
        this.loadingMore.set(false);
        this.hasMoreData = false; // Prevent infinite scroll looping on API failure
      },
    });
  }

  loadMore() {
    if (!this.loading() && !this.loadingMore() && this.hasMoreData) {
      this.pageIndex++;
      this.getUsers();
    }
  }

  onTableScroll(event: Event) {
    const target = event.target as HTMLElement;
    // Only load more if we have actually scrolled down a bit to prevent layout-shift loops at scrollTop=0
    if (target.scrollTop > 0 && target.scrollHeight - target.scrollTop - target.clientHeight < 100) {
      this.loadMore();
    }
  }

  onSearchChange() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.getUsers(true);
    }, 400);
  }

  onSortChange(event: { active: string; direction: 'asc' | 'desc' | '' }) {
    this.sortField = event.active;
    this.sortDirection = event.direction;
    this.getUsers(true);
  }

  isCurrentUser(userID: string): boolean {
    const currentId = this.currentUserData?._id?.toString() || this.currentUserData?.id?.toString();
    return !!currentId && currentId === userID?.toString();
  }

  openCreateDialog() {
    this.dialog
      .open(UserCreateComponent, {
        width: '480px',
        panelClass: 'standard-dialog-panel',
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.getUsers(true);
          this.loadPendingInvitations();
        }
      });
  }

  onRoleChange(user: ManagedUser, newRole: string) {
    this.authService.setWorkspaceUserRole(user._id, newRole).subscribe({
      next: () => {
        user.role = newRole;
        this.snackbar.open(this.translate.instant('Snackbar.ProfileUpdatedSuccessfully'), 'Ok', { duration: 2000 });
      },
      error: () => {
        this.snackbar.open(this.translate.instant('Snackbar.ErrorOccured'), 'Ok', { duration: 2000 });
      },
    });
  }

  deleteUser(user: ManagedUser, event: MouseEvent) {
    const target = event.currentTarget as HTMLElement;
    this.confirmMenuService.confirm(target, this.translate.instant('ManageUser.Delete'), 'delete_outline', '#ef4444').subscribe(() => {
      this.authService.removeUserFromWorkspace(user._id).subscribe(() => {
        this.getUsers(true);
      });
    });
  }

  // --- Pending Invitations ---

  loadPendingInvitations() {
    this.pendingInvitationsLoading.set(true);
    this.authService.getPendingInvitations().subscribe({
      next: (res) => {
        this.pendingInvitations.set(res.list);
        this.pendingInvitationsLoading.set(false);
      },
      error: (err) => {
        this.pendingInvitationsLoading.set(false);
        console.error('Failed to load pending invitations:', err);
      },
    });
  }

  resendInvite(invite: PendingInvitation) {
    const current = new Set(this.resendingIds());
    current.add(invite._id);
    this.resendingIds.set(current);

    this.authService.resendInvite(invite._id).subscribe({
      next: () => {
        const updated = new Set(this.resendingIds());
        updated.delete(invite._id);
        this.resendingIds.set(updated);
        this.snackbar.open(this.translate.instant('Snackbar.InvitationResent'), 'Ok', { duration: 2000 });
      },
      error: () => {
        const updated = new Set(this.resendingIds());
        updated.delete(invite._id);
        this.resendingIds.set(updated);
        this.snackbar.open(this.translate.instant('Snackbar.ErrorOccured'), 'Ok', { duration: 2000 });
      },
    });
  }

  cancelInvite(invite: PendingInvitation, event: MouseEvent) {
    const target = event.currentTarget as HTMLElement;
    this.confirmMenuService.confirm(target, this.translate.instant('UserManagement.CancelInvite'), 'close', '#ef4444').subscribe(() => {
      this.authService.cancelInvite(invite._id).subscribe({
        next: () => {
          this.pendingInvitations.update((list) => list.filter((i) => i._id !== invite._id));
          this.snackbar.open(this.translate.instant('Snackbar.InvitationCancelled'), 'Ok', { duration: 2000 });
        },
        error: () => {
          this.snackbar.open(this.translate.instant('Snackbar.ErrorOccured'), 'Ok', { duration: 2000 });
        },
      });
    });
  }

  isResending(inviteId: string): boolean {
    return this.resendingIds().has(inviteId);
  }

  togglePendingInvitationsBanner() {
    const nextState = !this.showPendingInvitationsBanner();
    this.showPendingInvitationsBanner.set(nextState);
    if (nextState) {
      this.showJoinLinkBanner.set(false);
      this.loadPendingInvitations();
    }
  }

  // --- Shareable Join Links ---

  loadJoinLinks() {
    this.joinLinksLoading.set(true);
    this.authService.getJoinLinks().subscribe({
      next: (res) => {
        this.joinLinksLoading.set(false);
        if (res.success) {
          this.joinLinks.set(res.links || []);
        }
      },
      error: (err) => {
        this.joinLinksLoading.set(false);
        console.error('Failed to load join links:', err);
      },
    });
  }

  toggleJoinLinkBanner() {
    if (this.joinLinks().length === 0) {
      this.loadJoinLinks();
    }
    const nextState = !this.showJoinLinkBanner();
    this.showJoinLinkBanner.set(nextState);
    if (nextState) {
      this.showPendingInvitationsBanner.set(false);
    }
  }

  openCreateJoinLinkDialog() {
    const ref = this.dialog.open(JoinLinkDialogComponent, {
      width: '460px',
    });
    ref.afterClosed().subscribe((saved) => {
      if (saved) {
        this.loadJoinLinks();
      }
    });
  }

  openEditJoinLinkDialog(link: JoinLinkItem) {
    const ref = this.dialog.open(JoinLinkDialogComponent, {
      width: '460px',
      data: { link },
    });
    ref.afterClosed().subscribe((saved) => {
      if (saved) {
        this.loadJoinLinks();
      }
    });
  }

  toggleJoinLinkItemStatus(link: JoinLinkItem) {
    const nextState = !link.enabled;
    this.authService.updateJoinLink(link._id, { enabled: nextState }).subscribe({
      next: (res) => {
        if (res.success) {
          this.joinLinks.update((list) => list.map((item) => (item._id === link._id ? { ...item, enabled: nextState } : item)));
          const msgKey = nextState ? 'Snackbar.JoinLinkEnabled' : 'Snackbar.JoinLinkDisabled';
          this.snackbar.open(this.translate.instant(msgKey), 'Ok', { duration: 2000 });
        }
      },
      error: () => {
        this.snackbar.open(this.translate.instant('Snackbar.ErrorOccured'), 'Ok', { duration: 2000 });
      },
    });
  }

  deleteJoinLinkItem(link: JoinLinkItem, event: MouseEvent) {
    const target = event.currentTarget as HTMLElement;
    this.confirmMenuService.confirm(target, this.translate.instant('UserManagement.ConfirmDeleteJoinLink'), 'delete', '#ef4444').subscribe(() => {
      this.authService.deleteJoinLink(link._id).subscribe({
        next: () => {
          this.joinLinks.update((list) => list.filter((item) => item._id !== link._id));
          this.snackbar.open(this.translate.instant('Snackbar.JoinLinkDeleted'), 'Ok', { duration: 2000 });
        },
        error: () => {
          this.snackbar.open(this.translate.instant('Snackbar.ErrorOccured'), 'Ok', { duration: 2000 });
        },
      });
    });
  }

  async openQrModalForLink(link: JoinLinkItem) {
    try {
      const qrUrl = await QRCode.toDataURL(link.joinUrl, {
        width: 600,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#ffffff',
        },
      });

      this.dialog.open(ImageDialogComponent, {
        data: {
          imageUrl: qrUrl,
          description: `${link.name}: ${link.joinUrl}`,
          showControls: false,
          initialScale: 1,
          minScale: 0.3,
        },
        panelClass: 'mermaid-dialog-panel',
        width: '80vmin',
        height: '80vmin',
        maxWidth: '90vw',
        maxHeight: '90vh',
      });
    } catch (err) {
      console.error('Failed to generate QR code locally:', err);
    }
  }

  copyLinkUrl(url: string) {
    navigator.clipboard.writeText(url);
    this.snackbar.open(this.translate.instant('Snackbar.JoinLinkCopied'), 'Ok', { duration: 2000 });
  }

  getGroupName(groupId?: string | null): string {
    if (!groupId) return this.translate.instant('UserManagement.DefaultGeneralGroup');
    const group = this.availableGroupsList().find((g) => String(g._id) === String(groupId));
    return group ? group.name : this.translate.instant('UserManagement.DefaultGeneralGroup');
  }

  ngOnDestroy(): void {
    this.sidenavService.showGlobalToggle.set(true);
  }
}
